/**
 * Data classification groups for employee data.
 *
 * Each section of employee data belongs to exactly one group.
 * Groups will later drive read / write / delete permission checks.
 *
 * Current groups:
 *  - gdpr          : personal info + address (GDPR-regulated PII)
 *  - contract      : identity documents (contract-related)
 *  - classified_2  : contact, emergency contact, dependents (sensitive level 2)
 */

export type DataGroupKey = 'gdpr' | 'contract' | 'classified_2';

export type DataGroup = {
  key: DataGroupKey;
  /** i18n key for the group display name */
  labelKey: string;
  /** i18n key for the group description */
  descriptionKey: string;
};

export const DATA_GROUPS: Record<DataGroupKey, DataGroup> = {
  gdpr: {
    key: 'gdpr',
    labelKey: 'dataGroups.gdpr.label',
    descriptionKey: 'dataGroups.gdpr.description',
  },
  contract: {
    key: 'contract',
    labelKey: 'dataGroups.contract.label',
    descriptionKey: 'dataGroups.contract.description',
  },
  classified_2: {
    key: 'classified_2',
    labelKey: 'dataGroups.classified_2.label',
    descriptionKey: 'dataGroups.classified_2.description',
  },
};

export type GroupAccess = {
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
};

/**
 * Returns the access rights for a given data group.
 *
 * **Stub implementation** — always grants full access.
 * When the permission system is ready, replace only this function body.
 * The signature (DataGroupKey → GroupAccess) is the stable contract.
 *
 * @example
 *   const { canRead } = useDataGroupAccess('gdpr');
 *   if (!canRead) return null;
 */
export function useDataGroupAccess(_group: DataGroupKey): GroupAccess {  // eslint-disable-line @typescript-eslint/no-unused-vars
  // TODO: derive from user role / permission store
  return { canRead: true, canWrite: true, canDelete: true };
}
